/**
 * Roles inside an Enterprise organization.
 *
 * The value is written to the user's `enterpriseRole` custom claim by the
 * backend (PUT /enterprise/users/:uid) — never straight to Firestore, or the
 * claim and the document drift apart and permissions silently stop matching.
 *
 * The owner (the account that pays for the subscription) is not a role: they
 * are identified by NOT having an `enterpriseUid`, and always have full access.
 */
export enum EnterpriseRole {
  /** Full organization control: users, roles, and every project. */
  ADMIN = 'admin',
  /** Sees and manages all organization projects, but cannot manage users. */
  MANAGER = 'manager',
  /** Default. Own projects plus anything shared with them. */
  USER = 'user',
  /** Read-only: can open and export projects, but not draw or edit. */
  VIEWER = 'viewer',
}

/** Capabilities a role grants. Keep every gate in the app derived from this. */
export interface EnterprisePermissions {
  /** Add, invite, edit, unlink and delete organization users. */
  manageUsers: boolean;
  /** Change other members' roles. */
  manageRoles: boolean;
  /** See every project in the organization, not just their own/shared. */
  viewAllProjects: boolean;
  /** Move/transfer projects between members. */
  transferProjects: boolean;
  /** Create new projects and edit geometry. */
  editProjects: boolean;
  /** Delete projects. */
  deleteProjects: boolean;
  /** Manage the organization's product/pricing catalogue. */
  manageProducts: boolean;
}

const PERMISSIONS: { [role: string]: EnterprisePermissions } = {
  [EnterpriseRole.ADMIN]: {
    manageUsers: true,
    manageRoles: true,
    viewAllProjects: true,
    transferProjects: true,
    editProjects: true,
    deleteProjects: true,
    manageProducts: true,
  },
  [EnterpriseRole.MANAGER]: {
    manageUsers: false,
    manageRoles: false,
    viewAllProjects: true,
    transferProjects: true,
    editProjects: true,
    deleteProjects: true,
    manageProducts: true,
  },
  [EnterpriseRole.USER]: {
    manageUsers: false,
    manageRoles: false,
    viewAllProjects: false,
    transferProjects: false,
    editProjects: true,
    deleteProjects: true,
    manageProducts: false,
  },
  [EnterpriseRole.VIEWER]: {
    manageUsers: false,
    manageRoles: false,
    viewAllProjects: false,
    transferProjects: false,
    editProjects: false,
    deleteProjects: false,
    manageProducts: false,
  },
};

/** Owner / non-enterprise accounts get everything. */
export const FULL_PERMISSIONS: EnterprisePermissions = PERMISSIONS[EnterpriseRole.ADMIN];

export function getPermissions(role: string): EnterprisePermissions {
  return PERMISSIONS[role] ?? PERMISSIONS[EnterpriseRole.USER];
}

/**
 * A role's rank (viewer 0 .. admin 3), as the API ranks it; an unknown role
 * ranks as a member. An Admin acts only on members ranked below them and
 * cannot hand out their own rank: only the owner manages Admins.
 */
export function roleRank(role: string): number {
  const ranks: { [role: string]: number } = {
    [EnterpriseRole.VIEWER]: 0,
    [EnterpriseRole.USER]: 1,
    [EnterpriseRole.MANAGER]: 2,
    [EnterpriseRole.ADMIN]: 3,
  };
  return ranks[role] ?? ranks[EnterpriseRole.USER];
}

/** Roles an owner/admin may assign, in order of decreasing authority. */
export const ASSIGNABLE_ROLES: EnterpriseRole[] = [
  EnterpriseRole.ADMIN,
  EnterpriseRole.MANAGER,
  EnterpriseRole.USER,
  EnterpriseRole.VIEWER,
];

/** i18n key + icon for each role, so the UI never hardcodes labels. */
export const ROLE_META: { [role: string]: { labelKey: string; descKey: string; icon: string } } = {
  [EnterpriseRole.ADMIN]: {
    labelKey: 'role_admin', descKey: 'role_admin_desc', icon: 'admin_panel_settings'
  },
  [EnterpriseRole.MANAGER]: {
    labelKey: 'role_manager', descKey: 'role_manager_desc', icon: 'supervisor_account'
  },
  [EnterpriseRole.USER]: {
    labelKey: 'role_member', descKey: 'role_member_desc', icon: 'person'
  },
  [EnterpriseRole.VIEWER]: {
    labelKey: 'role_viewer', descKey: 'role_viewer_desc', icon: 'visibility'
  },
};
